import { Api } from '@automapa/svelte-automapa-template';

import { UsageStatsApi, type UsageRowType } from './UsageStatsApi';

/**
 * Miesiac poprzedzajacy podany okres 'YYYY-MM'.
 *
 * Arytmetyka idzie NA NAPISIE, bez `new Date`: konstruktor daty interpretowalby 'YYYY-MM' jako
 * polnoc UTC, wiec w strefach na zachod od Greenwich `getMonth()` oddawalby miesiac wczesniejszy.
 */
export function previousMonth(period: string): string {
    const [year, month] = period.split('-').map(Number);

    return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, '0')}`;
}

/**
 * Biezacy miesiac w formacie 'YYYY-MM' wedlug zegara przegladarki.
 */
export function currentMonth(): string {
    const now = new Date();

    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Metryka zestawiona z limitem konta: 'uzycie / limit'.
 *
 * Dwie postacie mianownika: `limit === null` to BRAK limitu, renderowany jako '∞' (uwaga: `0` to
 * limit ZEROWY, nie brak), w kazdym innym razie sama liczba.
 *
 * Licznik NIE mowi juz, czy limit jest egzekwowany - limit miekki mial kiedys mianownik w nawiasie,
 * ale to rozroznienie niesie teraz osobna kolumna tabeli ('Limits enforced'), zeby ta sama
 * informacja nie mieszkala w dwoch miejscach.
 */
export function formatUsage(value: number, limit: number | null): string {
    if (limit === null) {
        return `${value} / ∞`;
    }

    return `${value} / ${limit}`;
}

/**
 * Uzycie metryki wyrazone w procentach limitu - `null`, gdy limitu nie ma.
 *
 * Limit ZEROWY jest odsiany osobna galezia, zeby nie dzielic przez zero: przy `limit === 0` kazde
 * uzycie jest przekroczeniem (`isLimitReached(x, 0) === true`), wiec `0` oddaje dokladnie 100 (limit
 * dobity), a wszystko powyzej - wartosc WIEKSZA od 100, czyli czerwony procent w widoku. Pasek i tak
 * jest ucinany do 100%, wiec konkretna liczba ponad setke ma znaczenie wylacznie dla koloru.
 *
 * Zaokraglanie zostaje po stronie widoku - tu liczy sie sama arytmetyka.
 */
export function usagePercent(value: number, limit: number | null): number | null {
    if (limit === null) {
        return null;
    }

    if (limit === 0) {
        return value === 0 ? 100 : 100 + value * 100;
    }

    return (value / limit) * 100;
}

/**
 * Czy klient wykonal w tym miesiacu jakakolwiek REALNA prace.
 *
 * `location_max` i `connection_max` sa tu POMINIETE swiadomie: cron (Cron::recordMonthlyUsage)
 * dopisuje ich snapshot co miesiac kazdemu klientowi, ktory ma niewykasowane punkty lub relacje
 * w bazie - takze kontu dawno martwemu. Oparcie filtru na nich znaczyloby, ze wygasle konto
 * z zaimportowanymi danymi widac juz na zawsze, we wszystkich przyszlych miesiacach.
 *
 * `recalc_sum` wystarcza za `recalc_manual` + `recalc_auto`, bo backend liczy je jako sume tamtych.
 */
export function hasActivity(row: UsageRowType): boolean {
    return row.transaction_count > 0 || row.export_sum > 0 || row.recalc_sum > 0;
}

/**
 * Czy wiersz ma sie pokazac w tabeli.
 *
 * Konto wygasle znika, ale TYLKO z miesiecy, w ktorych nic nie robilo - inaczej z tabel za
 * poprzednie okresy wyparowalaby historia klientow, ktorzy w miedzyczasie wygasli. Status
 * wygasniecia jest zawsze BIEZACY (settings nie ma historii), wiec to `hasActivity()` niesie
 * tu cala wiedze o przeszlosci.
 *
 * `showExpired` to przelacznik admina: pokazuje komplet, gdy trzeba sprawdzic, czemu konto zniklo.
 */
export function isRowVisible(row: UsageRowType, showExpired: boolean): boolean {
    return showExpired || !row.is_expired || hasActivity(row);
}

/**
 * Czy metryka dobila do limitu konta.
 *
 * Porownanie jest '>=', a nie '>': przy ROWNOSCI routing i import juz nie przepuszczaja
 * (RoutingManager::getAvailableTransactionCount() liczy max(0, limit - count)), wiec osiagniecie
 * limitu jest dokladnie tym momentem, ktory admin ma zobaczyc podswietlony.
 *
 * Brak limitu (`null`) nigdy nie jest osiagniety - takze przy limicie miekkim podswietlamy,
 * bo liczba faktycznie przekroczyla granice, nawet jesli konto ma na to zgode.
 */
export function isLimitReached(value: number, limit: number | null): boolean {
    return limit !== null && value >= limit;
}

/**
 * Stan zakladki 'usage-stats' na /admin (wzorzec lastmile UsageCollectorApp).
 *
 * `load()` i `loadNextMonth()` nigdy nie rzucaja - blad ladnie ląduje w `_error`, zeby komponent
 * mogl pokazac MessageDanger zamiast wywalac cala strone admina.
 */
export class UsageStatsApp {
    private readonly api: Api;
    private usageData: UsageRowType[] = [];
    private _isLoading: boolean = true;
    private _isLoadingMore: boolean = false;
    private _error: string | null = null;

    constructor(api: Api) {
        this.api = api;
    }

    /**
     * BillingEngine liczy JEDEN okres na wywolanie, wiec wejscie w zakladke pobiera wylacznie
     * biezacy miesiac. Starsze okresy dociaga `loadNextMonth()`.
     */
    public async load(): Promise<void> {
        this.usageData = await this.fetchMonth(currentMonth());
        this._isLoading = false;
    }

    /**
     * Dokłada miesiac starszy od najstarszego juz wczytanego. Nie ma dolnego limitu - okresy
     * sprzed poczatku danych wracaja jako komplet wierszy z zerami (lista klientow idzie z
     * `customer_config`, nie z `billing_event`) i to jest naturalny sygnal "dalej nic nie ma".
     */
    public async loadNextMonth(): Promise<void> {
        const months = this.getUniqueMonths();
        const oldest = months.length > 0 ? months[months.length - 1] : currentMonth();

        this._isLoadingMore = true;
        // dociagniete wiersze DOKLADAJA sie do juz wczytanych - blad zostawia tabele nietkniete
        this.usageData = [...this.usageData, ...(await this.fetchMonth(previousMonth(oldest)))];
        this._isLoadingMore = false;
    }

    private async fetchMonth(period: string): Promise<UsageRowType[]> {
        this._error = null;

        return UsageStatsApi.collectUsageData(this.api, period).catch(error => {
            this._error = error?.message || 'Failed to load usage data';
            console.error('Error loading usage data:', this._error);
            return [];
        });
    }

    public getUsageData(): UsageRowType[] {
        return this.usageData;
    }

    /**
     * Wiersze JEDNEGO miesiaca, juz przefiltrowane regula widocznosci.
     *
     * Widok pyta o gotowa liste, a nie filtruje w szablonie, bo musi rozpoznac miesiac, z ktorego
     * po odsianiu nie zostalo nic - pusta tabela bez slowa wyjasnienia wygladalaby jak blad
     * ladowania.
     */
    public getVisibleRows(month: string, showExpired: boolean): UsageRowType[] {
        return this.usageData.filter(row => row.month === month && isRowVisible(row, showExpired));
    }

    /**
     * Unikalne miesiace posortowane MALEJACO. Format 'YYYY-MM' sortuje sie leksykalnie tak samo
     * jak chronologicznie, wiec nie ma tu parsowania daty (lastmile robi `new Date(month)`).
     */
    public getUniqueMonths(): string[] {
        const months = this.usageData.map(row => row.month);
        return Array.from(new Set(months)).sort((a, b) => b.localeCompare(a));
    }

    public isLoading(): boolean {
        return this._isLoading;
    }

    /**
     * Osobny od `isLoading()`, zeby spinner pierwszego ladowania nie zaslanial juz wczytanych tabel.
     */
    public isLoadingMore(): boolean {
        return this._isLoadingMore;
    }

    public hasError(): boolean {
        return this._error !== null;
    }

    public getError(): string | null {
        return this._error;
    }
}
